import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listStoreRemovalRequests, dismissStoreRemoval, deleteStore } from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { PAGE_SIZE } from "@/lib/constants";
import { formatDateTime } from "@/lib/utils/date";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CheckCircle, Trash2 } from "lucide-react";

type Action = { type: "dismiss" | "delete"; store: Store };

export function StoreRemovalRequestsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState<Action | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["removal-requests", offset],
    queryFn: async () => listStoreRemovalRequests(user!, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
  });

  const dismissMutation = useMutation({
    mutationFn: async (storeId: string) => dismissStoreRemoval(user!, storeId),
    onSuccess: (_, storeId) => {
      void qc.invalidateQueries({ queryKey: ["removal-requests"] });
      void qc.invalidateQueries({ queryKey: ["stores"] });
      void qc.invalidateQueries({ queryKey: ["store", storeId] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      setAction(null);
      toast.success("Removal request dismissed. Store re-enabled.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (storeId: string) => deleteStore(user!, storeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["removal-requests"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      setAction(null);
      setDeleteConfirmName("");
      toast.success("Store permanently deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const isPending = dismissMutation.isPending || deleteMutation.isPending;

  const columns: Column<Store>[] = [
    {
      key: "name",
      header: "Store",
      render: (s) => (
        <button
          className="font-medium text-sm hover:underline text-left"
          onClick={(e) => {
            e.stopPropagation();
            void navigate(`/stores/${s.id}`);
          }}
        >
          {s.name}
        </button>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (s) => <span className="text-sm capitalize">{s.category}</span>,
    },
    {
      key: "requested",
      header: "Requested At",
      render: (s) => (
        <span className="text-sm text-muted-foreground">
          {s.removal_requested_at ? formatDateTime(s.removal_requested_at) : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (s) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setAction({ type: "dismiss", store: s });
            }}
          >
            <CheckCircle className="size-3.5 mr-1" /> Dismiss
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setAction({ type: "delete", store: s });
            }}
          >
            <Trash2 className="size-3.5 mr-1" /> Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Store Removal Requests</h1>
        <p className="text-sm text-muted-foreground">
          Stores where the owner has requested removal
        </p>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(s) => s.id}
        emptyMessage="No removal requests."
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}

      <ConfirmDialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAction(null);
            setDeleteConfirmName("");
          }
        }}
        title={action?.type === "dismiss" ? "Dismiss removal request" : "Permanently delete store"}
        description={
          action?.type === "dismiss"
            ? `This will re-enable "${action?.store.name}" and dismiss the removal request.`
            : `This will permanently delete "${action?.store.name}" and all its data. This cannot be undone.`
        }
        confirmLabel={action?.type === "dismiss" ? "Dismiss & Re-enable" : "Delete Permanently"}
        destructive={action?.type === "delete"}
        isPending={isPending}
        onConfirm={() => {
          if (!action) return;
          if (action.type === "dismiss") {
            dismissMutation.mutate(action.store.id);
          } else {
            deleteMutation.mutate(action.store.id);
          }
        }}
        confirmInput={
          action?.type === "delete"
            ? {
                value: deleteConfirmName,
                onChange: setDeleteConfirmName,
                placeholder: action.store.name,
                requiredValue: action.store.name,
              }
            : undefined
        }
      />
    </div>
  );
}
